import { useMutation, useQueryClient } from 'react-query';

import { memoriesQueryKey } from 'src/Hooks/Queries/useFetchMemories';
import { memoryQueryKey } from 'src/Hooks/Queries/useFetchMemory';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { memoriesApi } from 'src/Services/API';
import {
  AddMemoryRecordingInput,
  CreateMemoryInput,
  MemoryDto,
  MemoryRecordingDto,
  UpdateMemoryInput,
} from 'src/Services/API/Memories.api';

/** Memories also feed the Ma communauté strip and the "Lié à" panels. */
const useInvalidateMemories = () => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();
  return (memoryId?: string) => {
    queryClient.invalidateQueries(memoriesQueryKey(assetId));
    queryClient.invalidateQueries(['community-feed', assetId, 'memories']);
    queryClient.invalidateQueries(['linked-entities', assetId]);
    if (memoryId) queryClient.invalidateQueries(memoryQueryKey(assetId, memoryId));
  };
};

export const useCreateMemory = () => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateMemories();
  return useMutation<MemoryDto, unknown, CreateMemoryInput>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return memoriesApi.create(assetId, input);
    },
    { onSuccess: memory => invalidate(memory.id) }
  );
};

export const useUpdateMemory = (memoryId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateMemories();
  return useMutation<MemoryDto, unknown, UpdateMemoryInput>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return memoriesApi.update(assetId, memoryId, input);
    },
    { onSuccess: () => invalidate(memoryId) }
  );
};

export const useDeleteMemory = (memoryId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateMemories();
  return useMutation<void, unknown, void>(
    async () => {
      if (!assetId) throw new Error('No assetId provided');
      return memoriesApi.remove(assetId, memoryId);
    },
    { onSuccess: () => invalidate() }
  );
};

export const useAddMemoryRecording = (memoryId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateMemories();
  return useMutation<MemoryRecordingDto, unknown, AddMemoryRecordingInput>(
    async input => {
      if (!assetId) throw new Error('No assetId provided');
      return memoriesApi.addRecording(assetId, memoryId, input);
    },
    { onSuccess: () => invalidate(memoryId) }
  );
};

export const useDeleteMemoryRecording = (memoryId: string) => {
  const assetId = useCurrentAssetId();
  const invalidate = useInvalidateMemories();
  return useMutation<void, unknown, string>(
    async recordingId => {
      if (!assetId) throw new Error('No assetId provided');
      return memoriesApi.removeRecording(assetId, memoryId, recordingId);
    },
    { onSuccess: () => invalidate(memoryId) }
  );
};
